import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./styles/Partnerdetail.css";
import "./styles/DellTechnologies.css";
import dellLogo from "../../assets/images/partners/Dell_Technologies-Logo.png";

function useCounter(target, duration = 2000, inView) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration, inView]);
  return count;
}
function useInView(threshold = 0.3) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const infraTiles = [
  {
    cat: "Servers", title: "Dell PowerEdge Servers",
    img: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=900&q=80",
    body: "Dell PowerEdge is the world's most deployed server platform — engineered for peak performance across virtualization, database, and AI workloads. From the compact R350 for remote offices to the high-density R760xa supporting 8 NVIDIA H100 GPUs for AI training, PowerEdge covers every compute requirement with consistent management via iDRAC9 and OpenManage.",
    specs: ["Intel Xeon Scalable / AMD EPYC", "Gen 14 Autonomous Management", "8 x NVIDIA H100/A100 GPU Support", "NVMe-oF Direct-Attach Storage", "OpenManage Enterprise Central", "Cyber Resilient Architecture"],
    rev: false,
  },
  {
    cat: "Storage", title: "Dell PowerStore & PowerMax Storage",
    img: "https://images.unsplash.com/photo-1597852074816-d933c7d2b988?auto=format&fit=crop&w=900&q=80",
    body: "Dell PowerStore delivers next-generation all-flash storage with AI-driven optimization, inline deduplication, and seamless horizontal scale-out — all in a compact 2U appliance. PowerMax is the world's fastest storage array, delivering up to 10M IOPS for Oracle, SAP, and mission-critical mainframe applications with six-nines availability.",
    specs: ["PowerStore: 2U All-Flash", "PowerMax: 10M IOPS, 6×9 Availability", "Inline Dedup & Compression", "NVMe / iSCSI / FC / NVMe-oF", "Native Cloud Tiering", "Autonomous Storage Operations"],
    rev: true,
  },
  {
    cat: "Hybrid Cloud", title: "Dell APEX — Infrastructure as a Service",
    img: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=900&q=80",
    body: "Dell APEX delivers on-premises cloud infrastructure on a consumption-based model — eliminating capital expenditure while giving you the performance and data sovereignty of on-premises hardware. APEX includes a complete portfolio of compute, storage, HCI, and data protection services managed through a unified APEX Console.",
    specs: ["Pay-Per-Use Consumption Model", "APEX Compute (VMware / Kubernetes)", "APEX Storage DM — All-Flash", "APEX Cloud Platform — AWS/Azure", "APEX Data Protection", "APEX Console — Unified Management"],
    rev: false,
  },
  {
    cat: "Edge Computing", title: "Dell EMC PowerEdge Edge Servers",
    img: "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=900&q=80",
    body: "Extend enterprise compute capabilities to the edge with Dell's rugged, compact edge server portfolio. Purpose-built for IoT, retail, manufacturing, and telecommunications edge deployments — delivering low-latency compute where connectivity to a central data center isn't guaranteed.",
    specs: ["XE8545 — GPU-Dense Edge", "EMC XR Series Ruggedized", "5G MEC Platform Support", "Compact 1U & 2U Form Factors", "Industrial Temperature Range", "Remote Management via iDRAC"],
    rev: true,
  },
];

const aiFeatures = [
  { icon: "🤖", title: "PowerEdge AI Factory", desc: "Dell's validated AI reference architectures support NVIDIA DGX, HGX, and PCIe GPU platforms — from single-node development to multi-rack AI training clusters." },
  { icon: "⚡", title: "GPU-Accelerated Storage", desc: "Dell PowerScale OneFS with NVIDIA GPUDirect Storage delivers GPU-to-storage bandwidth at line speed — eliminating CPU bottlenecks in AI training pipelines." },
  { icon: "🧠", title: "Dell AI for Storage", desc: "PowerStore's embedded AI engine automates performance optimization, capacity planning, and predictive failure detection — reducing storage administration by 50%+." },
  { icon: "🌐", title: "Multi-Cloud AI Deployment", desc: "Dell APEX Cloud Platforms on AWS and Azure allow AI workloads to burst from on-premises training infrastructure to cloud-based inference at scale." },
];

const deploySteps = [
  { icon: "🔍", title: "Discovery", desc: "Assess your current hardware estate, workload requirements, and refresh priorities." },
  { icon: "📐", title: "Design", desc: "Right-size Dell configurations for compute, storage, and networking across all tiers." },
  { icon: "💰", title: "Procure", desc: "Streamlined Dell purchasing through ALG Data Guard at competitive pricing with flexible terms." },
  { icon: "🏗️", title: "Configure", desc: "Factory imaging and pre-configuration for zero-touch deployment at your sites." },
  { icon: "🚀", title: "Deploy", desc: "Precision physical and virtual deployment by Dell-certified engineers on your timeline." },
  { icon: "⚙️", title: "Manage", desc: "Ongoing firmware, monitoring, capacity planning, and lifecycle management." },
];

const industries = [
  { icon: "🏥", name: "Healthcare", desc: "Dell EMC healthcare-validated storage for PACS, EHR, and clinical data — HIPAA-compliant with immutable backup on PowerProtect." },
  { icon: "🏦", name: "Financial Services", desc: "PowerMax for trading applications requiring sub-100μs latency, and high-availability server clusters for 24/7 transaction processing." },
  { icon: "🎓", name: "Education", desc: "PowerEdge servers for virtualized research computing, student VDI environments, and Dell APEX consumption-based models for academic budget cycles." },
  { icon: "🏛️", name: "Government", desc: "TAA-compliant Dell hardware, FIPS-validated storage encryption, and DISA STIG-hardened configurations for federal and DoD deployments." },
  { icon: "🏭", name: "Manufacturing", desc: "Dell EMC Industrial IoT edge servers for shop floor analytics, digital twin computing, and real-time production monitoring." },
  { icon: "🛒", name: "Retail", desc: "Compact edge servers for in-store computing, centralized PowerEdge for e-commerce infrastructure, and APEX for elastic seasonal capacity." },
  { icon: "🌐", name: "Media & Telecom", desc: "GPU-accelerated PowerEdge for media transcoding, high-capacity PowerScale for content repositories, and 5G MEC edge platforms." },
  { icon: "⚡", name: "Energy", desc: "Rugged XR-series PowerEdge for harsh field environments, and high-availability infrastructure for grid management and SCADA systems." },
];

const whyCards = [
  { icon: "🏆", title: "#1 Server & Storage Market Share", body: "Dell Technologies holds #1 global market share in both x86 servers and external storage — reflecting the industry's trust in PowerEdge and PowerStore platforms across mission-critical workloads." },
  { icon: "🔒", title: "Cyber Resilient Architecture", body: "Dell's Cyber Resilient Architecture bakes security into hardware from the factory — with cryptographically verified firmware, immutable audit logs, and supply chain integrity verification." },
  { icon: "♻️", title: "Lifecycle Management Excellence", body: "Dell's ProSupport and ProDeploy services cover the complete hardware lifecycle — from configuration and imaging through on-site support, health monitoring, and secure asset disposal." },
  { icon: "🤝", title: "ALG Data Guard Integration", body: "ALG Data Guard's Dell-certified engineers manage your entire Dell infrastructure — from procurement advisory through deployment, operations, patching, and end-of-life transition planning." },
  { icon: "💰", title: "APEX As-a-Service Flexibility", body: "Dell APEX transforms capital expenditure into predictable operating expense — giving organizations enterprise-grade on-premises infrastructure on a pay-as-you-consume financial model." },
  { icon: "🌍", title: "Global Support in 160+ Countries", body: "Dell's global support infrastructure provides next-business-day ProSupport on-site response across 160+ countries — ensuring uptime for globally distributed infrastructure." },
];

const certifications = [
  { icon: "🏆", name: "Dell Gold Partner", desc: "Gold-tier Dell Technologies authorized partner with full product and services access" },
  { icon: "⭐", name: "Dell EMC PowerEdge", desc: "Server sales and deployment specialist — certified across the PowerEdge portfolio" },
  { icon: "💾", name: "Dell EMC Storage", desc: "Authorized storage partner — PowerStore, PowerMax, and PowerScale certified" },
  { icon: "☁️", name: "Dell APEX Cloud", desc: "Authorized APEX as-a-Service partner for on-premises and cloud APEX deployments" },
];

const faqData = [
  { q: "What is Dell APEX and how does it differ from traditional hardware purchasing?", a: "Dell APEX is Dell Technologies' as-a-service portfolio that delivers on-premises infrastructure on a consumption or subscription basis. Unlike traditional hardware purchases where you pay capital expenditure upfront and own the equipment, APEX provides the performance of on-premises hardware with the financial flexibility of a cloud model — paying monthly based on committed or actual usage. Dell retains ownership, manages refresh cycles, and provides proactive support. APEX covers compute (VMware and Kubernetes environments), all-flash storage, hyperconverged infrastructure, data protection, and multi-cloud platforms through a unified APEX Console." },
  { q: "What is Dell PowerEdge's Cyber Resilient Architecture?", a: "Dell's Cyber Resilient Architecture is a hardware security framework that builds protection into every PowerEdge server from manufacturing through operation. It includes: cryptographically verified firmware using a Silicon Root of Trust that mathematically verifies BIOS integrity before boot, iDRAC9 with SecureBoot to prevent unauthorized OS loading, immutable System Event Logs that cannot be tampered with by attackers, Supply Chain Assurance with factory attestation verifying components haven't been modified in transit, and automated System Lockdown preventing unauthorized configuration changes to operating servers. ALG Data Guard enables and validates Cyber Resilient Architecture during every PowerEdge deployment." },
  { q: "What is the difference between Dell PowerStore and PowerMax?", a: "Dell PowerStore is an all-flash storage array designed for mid-range enterprise workloads — delivering excellent performance with intelligent storage services including autonomous data reduction, AI-driven optimization, and horizontal scale-out. PowerStore is ideal for virtualization, databases, VDI, and general enterprise applications. Dell PowerMax is Dell's flagship high-end storage platform designed for mission-critical workloads demanding maximum performance and availability — delivering up to 10 million IOPS, 350 GB/s bandwidth, and six-nines (99.9999%) availability. PowerMax is the choice for Fortune 500 organizations running Oracle Exadata, SAP HANA, mainframe, and tier-1 financial applications." },
  { q: "How does Dell handle hardware support and what ProSupport options are available?", a: "Dell ProSupport offers multiple tiers of enterprise support: ProSupport Basic covers next-business-day on-site response for hardware failures, ProSupport Plus adds proactive monitoring through SupportAssist with predictive failure detection and automated support case creation, and ProSupport Flex provides custom SLAs with dedicated technical account managers. ALG Data Guard recommends ProSupport Plus for all business-critical infrastructure — the predictive monitoring component has been shown to prevent 85% of potential hardware failures through proactive part replacement before failure occurs. ALG Data Guard can also act as the primary support escalation point, managing Dell support cases on your behalf." },
  { q: "Can ALG Data Guard manage our Dell infrastructure on an ongoing basis?", a: "Yes. ALG Data Guard offers comprehensive Dell infrastructure managed services including: hardware health monitoring via Dell OpenManage and SupportAssist, firmware and driver lifecycle management, storage capacity planning and performance reporting, server configuration management and compliance, backup and recovery operations on Dell PowerProtect, and quarterly infrastructure health reviews. We offer co-managed models for organizations with internal IT teams and fully managed models where ALG Data Guard acts as the primary infrastructure operator." },
];

const ArrowIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
);
const ChevronDown = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
);

export default function DellTechnologies() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [metricsRef, metricsInView] = useInView(0.3);
  const c1 = useCounter(1200, 2200, metricsInView);
  const c2 = useCounter(99, 2000, metricsInView);
  const c3 = useCounter(160, 2000, metricsInView);
  const c4 = useCounter(250, 2200, metricsInView);

  return (
    <div className="dt-page pd-page">

      {/* ── Identity Band ── */}
      <div className="pd-identity-band">
        <div className="pd-identity-inner">
          <img src={dellLogo} alt="Dell Technologies" className="pd-partner-logo" />
          <div className="pd-identity-divider" aria-hidden="true" />
          <div className="pd-identity-text">
            <div className="pd-eyebrow pd-eyebrow--dark">
              <span className="pd-live-dot pd-live-dot--dark" />
              Enterprise Infrastructure Partner
            </div>
            <h1 className="pd-identity-name">ALG Data Guard × Dell Technologies</h1>
            <p className="pd-identity-desc">
              Dell Technologies is the world's #1 provider of enterprise servers and storage — delivering the PowerEdge, PowerStore, PowerMax, and APEX portfolio trusted by Fortune 500 organizations globally. ALG Data Guard is a Dell Gold Certified Partner, providing advisory, procurement, deployment, and managed services across the full Dell Technologies infrastructure stack.
            </p>
          </div>
        </div>
      </div>

      {/* ── Hero ── */}
      <section className="dt-hero">
        <div className="dt-hero__lines" aria-hidden="true" />
        <div className="dt-hero__glow" aria-hidden="true" />
        <div className="dt-hero__inner">
          <div>
            <div className="dt-hero__kicker">
              <span className="dt-hero__kicker-dot" />
              Dell Gold Certified Partner — Servers & Storage
            </div>
            <h2 className="dt-hero__title">
              Infrastructure Built for <em>What's Next</em>
            </h2>
            <p className="dt-hero__body">
              Whether you're running virtual machines today, planning AI workloads tomorrow, or migrating to a hybrid cloud model — Dell Technologies delivers the servers, storage, and data center infrastructure that scales with your ambition. ALG Data Guard helps you select, deploy, and operate it.
            </p>
            <div className="dt-hero__actions">
              <button className="dt-btn dt-btn--blue" onClick={() => navigate("/contact")}>
                Request an Infrastructure Assessment <ArrowIcon />
              </button>
              <button className="dt-btn dt-btn--ghost-w" onClick={() => navigate("/contact")}>
                Explore Dell Portfolio
              </button>
            </div>
            <div className="dt-hero__stats">
              <div>
                <div className="dt-hero__stat-val">#1</div>
                <div className="dt-hero__stat-label">Global Server Market Share</div>
              </div>
              <div>
                <div className="dt-hero__stat-val">#1</div>
                <div className="dt-hero__stat-label">Global Storage Market Share</div>
              </div>
              <div>
                <div className="dt-hero__stat-val">Gold</div>
                <div className="dt-hero__stat-label">ALG Data Guard Partner Tier</div>
              </div>
            </div>
          </div>

          <div className="dt-product-preview">
            <img
              src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=900&q=80"
              alt="Dell PowerEdge Server Infrastructure"
              className="dt-product-preview__img"
            />
            <div className="dt-product-preview__specs">
              {[
                { val: "10M+", label: "IOPS (PowerMax)" },
                { val: "99.9999%", label: "Availability SLA" },
                { val: "8x GPU", label: "Per Node (H100)" },
                { val: "APEX", label: "As-a-Service Ready" },
              ].map(s => (
                <div key={s.val} className="dt-product-spec">
                  <div className="dt-product-spec__val">{s.val}</div>
                  <div className="dt-product-spec__label">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Infrastructure Tiles ── */}
      <section className="dt-infra">
        <div className="dt-shell">
          <div className="dt-infra__head">
            <span className="dt-label"><span className="dt-dot" /> Infrastructure Portfolio</span>
            <h2 className="dt-section-title">Dell Technologies Across Every Infrastructure Layer</h2>
            <p className="dt-section-sub">
              From edge to core to cloud, Dell Technologies delivers validated infrastructure platforms for every workload — with consistent management, security, and lifecycle services across all tiers.
            </p>
          </div>
        </div>
        <div className="dt-infra-tiles">
          {infraTiles.map(tile => (
            <div key={tile.title} className={`dt-infra-tile${tile.rev ? " dt-infra-tile--rev" : ""}`}>
              <div className="dt-infra-tile__img-side">
                <img src={tile.img} alt={tile.title} className="dt-infra-tile__img" />
                <span className="dt-infra-tile__cat">{tile.cat}</span>
              </div>
              <div className="dt-infra-tile__content">
                <span className="dt-label"><span className="dt-dot" /> {tile.cat}</span>
                <h3 className="dt-infra-tile__title">{tile.title}</h3>
                <p className="dt-infra-tile__body">{tile.body}</p>
                <div className="dt-infra-tile__specs">{tile.specs.map(s => <span key={s} className="dt-infra-tile__spec">{s}</span>)}</div>
                <button className="dt-btn dt-btn--outline" onClick={() => navigate("/contact")}>
                  Get a Quote <ArrowIcon />
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── AI Infrastructure ── */}
      <section className="dt-ai">
        <div className="dt-shell">
          <div className="dt-ai__inner">
            <div>
              <span className="dt-label" style={{ background:"rgba(0,118,206,0.2)", borderColor:"rgba(0,118,206,0.4)", color:"#6fb5e8" }}>
                <span className="dt-dot" /> AI-Ready Infrastructure
              </span>
              <h2 className="dt-ai__title">Build Your AI Factory on Dell</h2>
              <p className="dt-ai__body">
                Generative AI and machine learning demand infrastructure that didn't exist five years ago — massive GPU density, ultra-fast NVMe storage, and high-bandwidth networking between nodes. Dell's validated AI Factory reference architectures deliver production-ready AI infrastructure from day one, with ALG Data Guard's implementation expertise accelerating your path to production.
              </p>
              <div className="dt-ai__features">
                {aiFeatures.map(f => (
                  <div key={f.title} className="dt-ai-feature">
                    <span className="dt-ai-feature__icon">{f.icon}</span>
                    <div>
                      <div className="dt-ai-feature__title">{f.title}</div>
                      <p className="dt-ai-feature__desc">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="dt-btn dt-btn--blue" onClick={() => navigate("/contact")}>
                  Plan My AI Infrastructure <ArrowIcon />
                </button>
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1677442135703-1787eea5ce01?auto=format&fit=crop&w=900&q=80"
              alt="AI infrastructure and data center"
              className="dt-ai__img"
            />
          </div>
        </div>
      </section>

      {/* ── Modern Workplace ── */}
      <section className="dt-workplace">
        <div className="dt-shell">
          <div className="dt-workplace__inner">
            <div className="dt-workplace__img-wrap">
              <img
                src="https://images.unsplash.com/photo-1593642632559-0c6d3fc62b89?auto=format&fit=crop&w=900&q=80"
                alt="Dell modern workplace devices"
                className="dt-workplace__img"
              />
              <div className="dt-workplace__img-accent">
                <div className="dt-workplace__img-accent-val">WFH</div>
                <div className="dt-workplace__img-accent-label">Ready Platform</div>
              </div>
            </div>
            <div>
              <span className="dt-label"><span className="dt-dot" /> Modern Workplace</span>
              <h2 className="dt-section-title">Devices Your Workforce Can Depend On</h2>
              <p className="dt-section-sub" style={{ marginBottom:"1rem" }}>
                Dell's Latitude and Precision device portfolio delivers enterprise-grade performance, security, and manageability for every role — from the executive suite to the field technician. Every device ships with Dell's comprehensive support options and integrates seamlessly with Microsoft Intune and SCCM for centralized endpoint management.
              </p>
              <p className="dt-section-sub" style={{ marginBottom:"2rem" }}>
                ALG Data Guard provides image loading, MDM enrollment, and deployment services so devices arrive at your users' desks ready to use on day one — dramatically reducing IT staff deployment burden.
              </p>
              <div className="dt-workplace__devices">
                {["💻 Latitude Laptops", "🖥️ OptiPlex Desktops", "🎨 Precision Workstations", "📱 Latitude 2-in-1s", "🖱️ Dell Monitors", "🎮 Alienware Gaming"].map(d => (
                  <div key={d} className="dt-device-chip">{d}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Deployment Timeline ── */}
      <section className="dt-deploy">
        <div className="dt-shell">
          <div className="dt-deploy__head">
            <span className="dt-label"><span className="dt-dot" /> Delivery Process</span>
            <h2 className="dt-section-title">How ALG Data Guard Delivers Dell Infrastructure</h2>
            <p className="dt-section-sub" style={{ margin:"0 auto" }}>
              A structured, validated delivery process from requirements through to live production operation.
            </p>
          </div>
          <div className="dt-timeline">
            <div className="dt-timeline__line" aria-hidden="true" />
            <div className="dt-timeline__steps">
              {deploySteps.map((step) => (
                <div key={step.title} className="dt-timeline__step">
                  <div className="dt-timeline__num">{step.icon}</div>
                  <h3 className="dt-timeline__step-title">{step.title}</h3>
                  <p className="dt-timeline__step-desc">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Industries ── */}
      <section className="dt-industries">
        <div className="dt-shell">
          <div className="dt-industries__head">
            <span className="dt-label" style={{ background:"rgba(0,118,206,0.2)", borderColor:"rgba(0,118,206,0.4)", color:"#6fb5e8" }}>
              <span className="dt-dot" /> Industries We Serve
            </span>
            <h2 className="dt-section-title">Dell Infrastructure Across Every Sector</h2>
            <p className="dt-section-sub">Dell's industry-validated infrastructure platforms meet the unique compliance, performance, and availability requirements of mission-critical environments globally.</p>
          </div>
          <div className="dt-industries__grid">
            {industries.map(ind => (
              <div key={ind.name} className="dt-industry-card">
                <span className="dt-industry-card__icon">{ind.icon}</span>
                <h3 className="dt-industry-card__name">{ind.name}</h3>
                <p className="dt-industry-card__desc">{ind.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why Dell ── */}
      <section className="dt-why">
        <div className="dt-shell">
          <div className="dt-why__head">
            <span className="dt-label"><span className="dt-dot" /> Business Value</span>
            <h2 className="dt-section-title">Why Leading Organizations Choose Dell Technologies</h2>
            <p className="dt-section-sub" style={{ margin:"0 auto" }}>Dell's global leadership in enterprise infrastructure delivers tangible business advantages beyond hardware performance.</p>
          </div>
          <div className="dt-why__grid">
            {whyCards.map(c => (
              <div key={c.title} className="dt-why-card">
                <span className="dt-why-card__icon">{c.icon}</span>
                <h3 className="dt-why-card__title">{c.title}</h3>
                <p className="dt-why-card__body">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Metrics ── */}
      <section className="dt-metrics" ref={metricsRef}>
        <div className="dt-shell">
          <div className="dt-metrics__inner">
            <div><div className="dt-metric__val">{c1}+</div><div className="dt-metric__label">Dell Systems Deployed</div></div>
            <div><div className="dt-metric__val">{c2}.9%</div><div className="dt-metric__label">Infrastructure Uptime</div></div>
            <div><div className="dt-metric__val">{c3}+</div><div className="dt-metric__label">Countries Supported</div></div>
            <div><div className="dt-metric__val">{c4}+</div><div className="dt-metric__label">Enterprise Clients</div></div>
          </div>
        </div>
      </section>

      {/* ── Certifications ── */}
      <section className="dt-certs">
        <div className="dt-shell">
          <div className="dt-certs__head">
            <span className="dt-label"><span className="dt-dot" /> Partner Credentials</span>
            <h2 className="dt-section-title">Certified Dell Technologies Partner</h2>
            <p className="dt-section-sub" style={{ margin:"0 auto" }}>ALG Data Guard's Dell Gold Partner status and product certifications validate our expertise across the full Dell infrastructure portfolio.</p>
          </div>
          <div className="dt-certs__grid">
            {certifications.map(c => (
              <div key={c.name} className="dt-cert-card">
                <span className="dt-cert-card__icon">{c.icon}</span>
                <div className="dt-cert-card__name">{c.name}</div>
                <p className="dt-cert-card__desc">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="dt-faq">
        <div className="dt-shell">
          <div className="dt-faq__head">
            <span className="dt-label" style={{ background:"rgba(0,118,206,0.2)", borderColor:"rgba(0,118,206,0.4)", color:"#6fb5e8", margin:"0 auto 1rem" }}>
              <span className="dt-dot" /> FAQs
            </span>
            <h2 className="dt-section-title" style={{ color:"#fff" }}>Dell Infrastructure Questions, Answered</h2>
            <p className="dt-section-sub" style={{ color:"var(--dt-text-light)", margin:"0 auto" }}>Expert answers about Dell Technologies products and ALG Data Guard's delivery approach.</p>
          </div>
          <div className="dt-faq__list">
            {faqData.map((faq, i) => (
              <div key={i} className={`dt-faq-item${openFaq === i ? " open" : ""}`}>
                <button className="dt-faq-item__trigger" onClick={() => setOpenFaq(openFaq === i ? null : i)} aria-expanded={openFaq === i}>
                  <span className="dt-faq-item__q">{faq.q}</span>
                  <span className="dt-faq-item__arrow"><ChevronDown /></span>
                </button>
                <div className="dt-faq-item__answer"><div className="dt-faq-item__answer-inner">{faq.a}</div></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="dt-cta">
        <div className="dt-cta__inner">
          <span className="dt-label" style={{ background:"rgba(0,118,206,0.25)", borderColor:"rgba(0,118,206,0.5)", color:"#6fb5e8", margin:"0 auto 1.2rem" }}>
            <span className="dt-dot" /> Get Started
          </span>
          <h2 className="dt-cta__title">Build the Infrastructure Your Business Runs On</h2>
          <p className="dt-cta__sub">
            Whether you're refreshing aging servers, modernizing storage, planning AI infrastructure, or migrating to APEX as-a-service — ALG Data Guard's Dell Gold Certified engineers are ready to design and deliver the right solution.
          </p>
          <div className="dt-cta__actions">
            <button className="dt-btn dt-btn--blue" onClick={() => navigate("/contact")}>
              Request an Infrastructure Assessment <ArrowIcon />
            </button>
            <button className="dt-btn dt-btn--ghost-w" onClick={() => navigate("/contact")}>
              Get a Dell Quote
            </button>
          </div>
        </div>
      </section>

    </div>
  );
}
